import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Alert,
  Snackbar,
  Grid,
  Card,
  CardContent,
  CardActions,
  Switch,
  FormControlLabel,
  Chip,
  Divider,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Cable as CableIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  ExpandMore as ExpandMoreIcon,
  Router as RouterIcon,
  Speed as SpeedIcon,
  Memory as MemoryIcon
} from '@mui/icons-material';
import axios from 'axios';

const OPCUAConfig = () => {
  const [config, setConfig] = useState({
    endpoint_url: '',
    security_policy: 'None',
    security_mode: 'None',
    username: '',
    password: ''
  });
  
  const [connectionStatus, setConnectionStatus] = useState({
    connected: false,
    last_connection: null,
    error_message: null,
    server_info: null
  });
  
  const [nodeVariables, setNodeVariables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  useEffect(() => {
    loadConfig();
    loadConnectionStatus();
    loadNodeVariables();
  }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/opcua/config');
      if (response.data.config) {
        setConfig(response.data.config);
      }
    } catch (error) {
      console.error('Errore nel caricamento configurazione OPC UA:', error);
      showSnackbar('Errore nel caricamento configurazione', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadConnectionStatus = async () => {
    try {
      const response = await axios.get('/api/opcua/status');
      // Map backend response to frontend state structure
      const backendStatus = response.data.status || response.data;
      setConnectionStatus({
        connected: backendStatus.connected || false,
        last_connection: backendStatus.timestamp || null,
        error_message: backendStatus.error || null,
        server_info: backendStatus.endpoint ? {
          productName: 'OPC UA Server',
          productVersion: '1.0',
          endpoint: backendStatus.endpoint
        } : null
      });
    } catch (error) {
      console.error('Errore nel caricamento stato connessione:', error);
      setConnectionStatus({
        connected: false,
        last_connection: null,
        error_message: 'Errore nel caricamento stato',
        server_info: null
      });
    }
  };

  const loadNodeVariables = async () => {
    // Simuliamo i nodi OPC-UA disponibili
    setNodeVariables([
      { displayName: 'Machine Status', nodeId: 'ns=2;s=Machine.Status', dataType: 'String' },
      { displayName: 'Production Count', nodeId: 'ns=2;s=Machine.ProductionCount', dataType: 'Int32' },
      { displayName: 'Order ID', nodeId: 'ns=2;s=Order.OrderId', dataType: 'String' },
      { displayName: 'Product Code', nodeId: 'ns=2;s=Order.ProductCode', dataType: 'String' },
      { displayName: 'Quantity', nodeId: 'ns=2;s=Order.Quantity', dataType: 'Int32' }
    ]);
  };

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleConfigChange = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveConfig = async () => {
    try {
      setSaving(true);
      await axios.post('/api/opcua/config', config);
      showSnackbar('Configurazione salvata con successo', 'success');
      loadConnectionStatus();
      loadConfig();
    } catch (error) {
      console.error('Errore nel salvataggio configurazione:', error);
      if (error.response?.data?.errors) {
        const errorMessages = error.response.data.errors.map(err => err.msg).join(', ');
        showSnackbar(`Errore di validazione: ${errorMessages}`, 'error');
      } else {
        showSnackbar('Errore nel salvataggio configurazione', 'error');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    try {
      setTesting(true);
      const response = await axios.post('/api/opcua/test', config);
      
      if (response.data.success) {
        showSnackbar('Connessione testata con successo', 'success');
      } else {
        showSnackbar(`Test fallito: ${response.data.message}`, 'error');
      }
      
      loadConnectionStatus();
    } catch (error) {
      console.error('Errore nel test connessione:', error);
      if (error.response?.data?.errors) {
        const errorMessages = error.response.data.errors.map(err => err.msg).join(', ');
        showSnackbar(`Errore di validazione: ${errorMessages}`, 'error');
      } else {
        showSnackbar('Errore nel test connessione', 'error');
      }
    } finally {
      setTesting(false);
    }
  };

  const handleToggleConnection = async () => {
    try {
      if (connectionStatus.connected) {
        await axios.post('/api/opcua/disconnect');
        showSnackbar('Connessione OPC UA chiusa', 'info');
      } else {
        await axios.post('/api/opcua/connect');
        showSnackbar('Connessione OPC UA avviata', 'success');
      }
      
      setTimeout(loadConnectionStatus, 1000);
    } catch (error) {
      console.error('Errore nel toggle connessione:', error);
      showSnackbar('Errore nella gestione connessione', 'error');
    }
  };

  const getStatusChip = () => {
    if (connectionStatus.connected) {
      return <Chip label="Connesso" color="success" icon={<CheckCircleIcon />} />;
    } else if (connectionStatus.error_message) {
      return <Chip label="Errore" color="error" icon={<ErrorIcon />} />;
    } else {
      return <Chip label="Disconnesso" color="default" icon={<WarningIcon />} />;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        <SettingsIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Configurazione OPC UA
      </Typography>
      
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Configura la connessione alla macchina operatrice tramite protocollo OPC UA
      </Typography>

      <Grid container spacing={3}>
        {/* Stato Connessione */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  <CableIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Stato Connessione
                </Typography>
                {getStatusChip()}
              </Box>
              
              {connectionStatus.error_message && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {connectionStatus.error_message}
                </Alert>
              )}
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Ultimo tentativo di connessione:
                  </Typography>
                  <Typography variant="body1">
                    {connectionStatus.last_connection 
                      ? new Date(connectionStatus.last_connection).toLocaleString('it-IT')
                      : 'Mai'
                    }
                  </Typography>
                </Grid>
                {connectionStatus.server_info && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">
                      Informazioni Server:
                    </Typography>
                    <Typography variant="body1">
                      {connectionStatus.server_info.productName} v{connectionStatus.server_info.productVersion}
                    </Typography>
                  </Grid>
                )}
              </Grid>
              
              <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  onClick={handleToggleConnection}
                  disabled={testing || saving}
                >
                  {connectionStatus.connected ? 'Disconnetti' : 'Connetti'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={loadConnectionStatus}
                  startIcon={<RefreshIcon />}
                >
                  Aggiorna
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Configurazione */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <RouterIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Parametri di Connessione
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Endpoint URL"
                    value={config.endpoint_url || ''}
                    onChange={(e) => handleConfigChange('endpoint_url', e.target.value)}
                    placeholder="opc.tcp://opcua-simulator:4840/UA/IndustrialMachineSimulator"
                    helperText="URL completo del server OPC UA"
                    required
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    select
                    label="Security Policy"
                    value={config.security_policy || 'None'}
                    onChange={(e) => handleConfigChange('security_policy', e.target.value)}
                    SelectProps={{ native: true }}
                  >
                    <option value="None">None</option>
                    <option value="Basic128Rsa15">Basic128Rsa15</option>
                    <option value="Basic256">Basic256</option>
                    <option value="Basic256Sha256">Basic256Sha256</option>
                  </TextField>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    select
                    label="Security Mode"
                    value={config.security_mode || 'None'}
                    onChange={(e) => handleConfigChange('security_mode', e.target.value)}
                    SelectProps={{ native: true }}
                  >
                    <option value="None">None</option>
                    <option value="Sign">Sign</option>
                    <option value="SignAndEncrypt">SignAndEncrypt</option>
                  </TextField>
                </Grid>
                
                {config.security_mode !== 'None' && (
                  <>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Username"
                        value={config.username || ''}
                        onChange={(e) => handleConfigChange('username', e.target.value)}
                        helperText="Lasciare vuoto per autenticazione anonima"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Password"
                        type="password"
                        value={config.password || ''}
                        onChange={(e) => handleConfigChange('password', e.target.value)}
                        helperText="Lasciare vuoto per autenticazione anonima"
                      />
                    </Grid>
                  </>
                )}
              </Grid>
            </CardContent>
            
            <CardActions>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSaveConfig}
                disabled={saving || testing}
              >
                {saving ? <CircularProgress size={20} /> : 'Salva Configurazione'}
              </Button>
              
              <Button
                variant="outlined"
                onClick={handleTestConnection}
                disabled={saving || testing || !config.endpoint_url}
              >
                {testing ? <CircularProgress size={20} /> : 'Test Connessione'}
              </Button>
            </CardActions>
          </Card>
        </Grid>

        {/* Variabili Nodo */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <SpeedIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Variabili Monitoraggio
              </Typography>
              
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>Variabili Disponibili ({nodeVariables.length})</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <List dense>
                    {nodeVariables.length > 0 ? (
                      nodeVariables.map((variable, index) => (
                        <ListItem key={index}>
                          <ListItemIcon>
                            <MemoryIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary={variable.displayName}
                            secondary={`${variable.nodeId} (${variable.dataType})`}
                          />
                        </ListItem>
                      ))
                    ) : (
                      <ListItem>
                        <ListItemText 
                          primary="Nessuna variabile trovata"
                          secondary="Connettiti al server per visualizzare le variabili"
                        />
                      </ListItem>
                    )}
                  </List>
                </AccordionDetails>
              </Accordion>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Snackbar per messaggi */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default OPCUAConfig;